
const html = document.documentElement;
html.classList.add('nx-js');

const mqReduce = matchMedia('(prefers-reduced-motion: reduce)');
const mqFine = matchMedia('(hover: hover) and (pointer: fine)');
const mqDesk = matchMedia('(min-width: 990px)');
const reduced = () => mqReduce.matches;
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

const getScroller = () => (mqDesk.matches ? document.querySelector('.page-wrapper') || window : window);
const scrollTop = () => {
  const s = getScroller();
  return s === window ? window.scrollY : s.scrollTop;
};
function onScroll(fn) {
  let current = null;
  const attach = () => {
    if (current) current.removeEventListener('scroll', fn);
    current = getScroller();
    current.addEventListener('scroll', fn, { passive: true });
  };
  attach();
  mqDesk.addEventListener('change', attach);
}

const active = new Set();
let raf = 0;
let last = 0;

function wake(spring) {
  active.add(spring);
  if (!raf) {
    last = performance.now();
    raf = requestAnimationFrame(frame);
  }
}

function frame(now) {
  const dt = Math.min(0.05, (now - last) / 1000);
  last = now;
  for (const s of [...active]) {
    const done = s.step(dt);
    if (s.onUpdate) s.onUpdate(s);
    if (done) {
      active.delete(s);
      if (s.onRest) s.onRest(s);
    }
  }
  raf = active.size ? requestAnimationFrame(frame) : 0;
}

class Spring {
  constructor(value = 0, { response = 0.4, damping = 1 } = {}) {
    this.x = value;
    this.v = 0;
    this.target = value;
    this.onUpdate = null;
    this.onRest = null;
    this.configure(response, damping);
  }
  configure(response, damping) {
    const w = (2 * Math.PI) / response;
    this.k = w * w;
    this.c = 2 * damping * w;
  }
  to(target, velocity) {
    this.target = target;
    if (velocity !== undefined) this.v = velocity;
    if (reduced()) {
      this.jump(target);
      if (this.onUpdate) this.onUpdate(this);
      return;
    }
    wake(this);
  }
  jump(x) {
    this.x = this.target = x;
    this.v = 0;
    active.delete(this);
  }
  step(dt) {
    const n = Math.max(1, Math.ceil(dt / 0.004));
    const h = dt / n;
    for (let i = 0; i < n; i++) {
      const a = this.k * (this.target - this.x) - this.c * this.v;
      this.v += a * h;
      this.x += this.v * h;
    }
    if (Math.abs(this.v) < 0.002 && Math.abs(this.target - this.x) < 0.0008) {
      this.x = this.target;
      this.v = 0;
      return true;
    }
    return false;
  }
}

const project = (v, rate = 0.998) => ((v / 1000) * rate) / (1 - rate);
const rubber = (over, dim, c = 0.55) => (over * dim * c) / (dim + c * Math.abs(over));


class NxRail extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-rail-viewport]');
    this.track = this.querySelector('[data-rail-track]');
    if (!this.viewport || !this.track || this.enhanced) return;
    this.enhanced = true;
    this.prev = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');
    this.bar = this.querySelector('[data-rail-bar]');
    this.setAttribute('data-enhanced', '');

    this.spring = new Spring(0, { response: 0.5, damping: 1 });
    this.spring.onUpdate = () => this.paint(this.spring.x);
    this.pos = 0;
    this.min = 0;
    this.snaps = [0];
    this.drag = null;
    this.moved = 0;

    this.measure();
    this.ro = new ResizeObserver(() => this.measure());
    this.ro.observe(this.viewport);
    this.ro.observe(this.track);

    this.viewport.addEventListener('pointerdown', this.onDown);
    this.viewport.addEventListener('click', this.onClickCapture, true);
    this.viewport.addEventListener('wheel', this.onWheel, { passive: false });
    this.viewport.addEventListener('keydown', this.onKey);
    this.viewport.addEventListener('focusin', this.onFocusIn);
    this.viewport.addEventListener('dragstart', (e) => e.preventDefault());
    if (this.prev) this.prev.addEventListener('click', () => this.go(-1));
    if (this.next) this.next.addEventListener('click', () => this.go(1));
    this.paint(0);
  }

  disconnectedCallback() {
    if (this.ro) this.ro.disconnect();
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onUp);
  }

  measure() {
    const vw = this.viewport.clientWidth;
    const tw = this.track.scrollWidth;
    this.vw = vw;
    this.min = Math.min(0, vw - tw);
    const items = [...this.track.querySelectorAll('[data-rail-item]')];
    const set = new Set([0]);
    for (const it of items) set.add(clamp(-it.offsetLeft, this.min, 0));
    set.add(this.min);
    this.snaps = [...set].sort((a, b) => b - a);
    this.pos = clamp(this.pos, this.min, 0);
    this.spring.jump(this.pos);
    this.paint(this.pos);
  }

  paint(x) {
    this.pos = x;
    this.track.style.transform = `translate3d(${x.toFixed(2)}px, 0, 0)`;
    const range = -this.min;
    const p = range > 0 ? clamp(-x / range, 0, 1) : 0;
    this.style.setProperty('--rail-p', p.toFixed(4));
    if (this.bar) {
      const w = this.track.scrollWidth > 0 ? clamp(this.vw / this.track.scrollWidth, 0.08, 1) : 1;
      this.bar.style.width = `${(w * 100).toFixed(2)}%`;
      this.bar.style.transform = `translate3d(${((1 / w - 1) * p * 100).toFixed(2)}%, 0, 0)`;
    }
    if (this.prev) this.prev.disabled = x >= -1;
    if (this.next) this.next.disabled = x <= this.min + 1 || range <= 0;
  }

  settle(target, velocity = 0) {
    const t = clamp(target, this.min, 0);
    this.spring.configure(Math.abs(velocity) > 900 ? 0.62 : 0.5, Math.abs(velocity) > 900 ? 0.82 : 1);
    this.spring.x = this.pos;
    this.spring.v = velocity;
    this.spring.to(t);
  }

  nearest(x) {
    let best = this.snaps[0];
    let d = Infinity;
    for (const s of this.snaps) {
      const dd = Math.abs(s - x);
      if (dd < d) {
        d = dd;
        best = s;
      }
    }
    return best;
  }

  go(dir) {
    const cur = this.spring.target;
    const list = this.snaps;
    let target = cur;
    if (dir > 0) target = [...list].reverse().find((s) => s < cur - 2) ?? this.min;
    else target = list.find((s) => s > cur + 2) ?? 0;
    this.settle(target, 0);
  }

  onDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    this.spring.jump(this.pos);
    active.delete(this.spring);
    this.drag = { id: e.pointerId, x0: e.clientX, y0: e.clientY, base: this.pos, samples: [{ t: e.timeStamp, x: e.clientX }], on: false };
    this.moved = 0;
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
  };

  onMove = (e) => {
    const d = this.drag;
    if (!d || e.pointerId !== d.id) return;
    const dx = e.clientX - d.x0;
    const dy = e.clientY - d.y0;
    if (!d.on) {
      if (Math.abs(dx) < 6) return;
      if (Math.abs(dy) > Math.abs(dx) && e.pointerType !== 'mouse') {
        this.finish();
        return;
      }
      d.on = true;
      this.setAttribute('data-dragging', '');
    }
    this.moved = Math.max(this.moved, Math.abs(dx));
    d.samples.push({ t: e.timeStamp, x: e.clientX });
    if (d.samples.length > 6) d.samples.shift();
    const raw = d.base + dx;
    let x = raw;
    if (raw > 0) x = rubber(raw, this.vw);
    else if (raw < this.min) x = this.min + rubber(raw - this.min, this.vw);
    this.paint(x);
  };

  onUp = (e) => {
    const d = this.drag;
    if (!d || (e && e.pointerId !== d.id)) return;
    let v = 0;
    if (d.on && d.samples.length > 1) {
      const a = d.samples[0];
      const b = d.samples[d.samples.length - 1];
      const dt = b.t - a.t;
      if (dt > 0 && e && e.type !== 'pointercancel') v = ((b.x - a.x) / dt) * 1000;
    }
    const wasDragging = d.on;
    this.finish();
    if (!wasDragging) return;
    const projected = this.pos + project(v, 0.995);
    this.settle(this.nearest(clamp(projected, this.min, 0)), v);
  };

  finish() {
    this.drag = null;
    this.removeAttribute('data-dragging');
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onUp);
  }

  onClickCapture = (e) => {
    if (this.moved > 6) {
      e.preventDefault();
      e.stopPropagation();
      this.moved = 0;
    }
  };

  onWheel = (e) => {
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) || this.min === 0) return;
    e.preventDefault();
    const x = clamp(this.pos - e.deltaX, this.min, 0);
    this.spring.jump(x);
    this.paint(x);
    clearTimeout(this.wheelTimer);
    this.wheelTimer = setTimeout(() => this.settle(this.nearest(this.pos), 0), 140);
  };

  onKey = (e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.go(1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.go(-1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      this.settle(0, 0);
    } else if (e.key === 'End') {
      e.preventDefault();
      this.settle(this.min, 0);
    }
  };

  onFocusIn = (e) => {
    const item = e.target.closest && e.target.closest('[data-rail-item]');
    if (!item || this.drag) return;
    const left = -this.pos;
    const right = left + this.vw;
    if (item.offsetLeft < left || item.offsetLeft + item.offsetWidth > right) {
      this.settle(clamp(-item.offsetLeft + 24, this.min, 0), 0);
    }
  };
}
if (!customElements.get('nx-rail')) customElements.define('nx-rail', NxRail);

function initHeader(scope = document) {
  const header = scope.querySelector('[data-nx-header]');
  if (!header || header.hasAttribute('data-nx-head-on')) return;
  header.setAttribute('data-nx-head-on', '');

  const check = () => header.toggleAttribute('data-scrolled', scrollTop() > 24);
  onScroll(check);
  check();

  const items = [...header.querySelectorAll('.nx-announce__item')];
  if (items.length) {
    let i = 0;
    items[0].classList.add('is-active');
    if (items.length > 1 && !reduced()) {
      let paused = false;
      const bar = header.querySelector('.nx-announce');
      bar.addEventListener('pointerenter', () => (paused = true));
      bar.addEventListener('pointerleave', () => (paused = false));
      bar.addEventListener('focusin', () => (paused = true));
      bar.addEventListener('focusout', () => (paused = false));
      setInterval(() => {
        if (paused || document.hidden) return;
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, 5200);
    }
  }

  const nav = header.querySelector('.nx-nav');
  if (nav && mqFine.matches) {
    const place = (el) => {
      const nr = nav.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      nav.style.setProperty('--pill-x', `${(r.left - nr.left).toFixed(1)}px`);
      nav.style.setProperty('--pill-w', `${r.width.toFixed(1)}px`);
      nav.setAttribute('data-pill', 'on');
    };
    nav.addEventListener('pointerover', (e) => {
      const el = e.target.closest('.nx-nav__link');
      if (el) place(el);
    });
    nav.addEventListener('focusin', (e) => {
      const el = e.target.closest('.nx-nav__link');
      if (el) place(el);
    });
    nav.addEventListener('pointerleave', () => nav.removeAttribute('data-pill'));
  }

  const trigger = header.querySelector('[data-nx-mega-trigger]');
  const mega = header.querySelector('[data-nx-mega]');
  if (trigger && mega) {
    let timer = 0;
    const open = () => {
      clearTimeout(timer);
      header.setAttribute('data-mega', 'open');
      trigger.setAttribute('aria-expanded', 'true');
    };
    const close = (delay = 180) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        header.removeAttribute('data-mega');
        trigger.setAttribute('aria-expanded', 'false');
      }, delay);
    };
    const hoverable = () => mqFine.matches;
    trigger.addEventListener('pointerenter', () => hoverable() && open());
    trigger.addEventListener('pointerleave', () => hoverable() && close());
    mega.addEventListener('pointerenter', () => hoverable() && open());
    mega.addEventListener('pointerleave', () => hoverable() && close());
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      header.getAttribute('data-mega') === 'open' ? close(0) : open();
    });
    header.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && header.getAttribute('data-mega') === 'open') {
        close(0);
        trigger.focus();
      }
    });
    document.addEventListener('pointerdown', (e) => {
      if (!header.contains(e.target)) close(0);
    });
    mega.addEventListener('focusout', (e) => {
      if (!mega.contains(e.relatedTarget) && e.relatedTarget !== trigger) close(60);
    });
  }

  const dialog = header.querySelector('[data-nx-menu]');
  const openBtn = header.querySelector('[data-nx-menu-open]');
  if (dialog && openBtn) {
    const lock = (on) => (on ? html.setAttribute('scroll-lock', '') : html.removeAttribute('scroll-lock'));
    openBtn.addEventListener('click', () => {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      openBtn.setAttribute('aria-expanded', 'true');
      lock(true);
    });
    const done = () => {
      openBtn.setAttribute('aria-expanded', 'false');
      lock(false);
    };
    dialog.addEventListener('close', done);
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog || e.target.closest('[data-nx-menu-close]') || e.target.closest('a')) dialog.close();
    });
  }
}


function initPrefetch() {
  if (!mqFine.matches || (navigator.connection && navigator.connection.saveData)) return;
  const done = new Set();
  let timer = 0;
  document.addEventListener(
    'pointerover',
    (e) => {
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a || a.origin !== location.origin || a.hash || a.target === '_blank') return;
      const url = a.href;
      if (done.has(url) || done.size > 24 || /\/(cart|account|checkout)/.test(a.pathname)) return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        done.add(url);
        const l = document.createElement('link');
        l.rel = 'prefetch';
        l.href = url;
        document.head.appendChild(l);
      }, 70);
    },
    { passive: true }
  );
  document.addEventListener('pointerout', () => clearTimeout(timer), { passive: true });
}

function init() {
  initHeader(document);
}

function boot() {
  init();
  initPrefetch();
  html.classList.add('nx-ready');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();

document.addEventListener('shopify:section:load', () => initHeader(document));
